import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={cx(
        `
          w-full rounded-2xl
          border border-[rgb(var(--border))]
          bg-[rgb(var(--bg-app))]
          px-4 py-3.5
          text-[rgb(var(--text-main))]
          placeholder:text-[rgb(var(--text-muted))]
          outline-none transition
          focus:border-[rgb(var(--brand)/0.55)]
          focus:ring-4 focus:ring-[rgb(var(--brand)/0.10)]
        `,
        className
      )}
    />
  );
}

function PrimaryButton({ children, disabled }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className={cx(
        `
          inline-flex w-full items-center justify-center
          rounded-2xl border
          px-5 py-3.5
          text-sm font-extrabold
          transition
        `,
        disabled
          ? `
              cursor-not-allowed
              border-[rgb(var(--border))]
              bg-[rgb(var(--elevated))]
              text-[rgb(var(--text-muted))]
            `
          : `
              border-[rgb(var(--brand)/0.35)]
              bg-[rgb(var(--brand))]
              text-white
              shadow-[var(--shadow-soft)]
              hover:-translate-y-0.5
              hover:opacity-90
            `
      )}
    >
      {children}
    </button>
  );
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverMessage, setServerMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");
    setServerMessage("");

    try {
      const res = await axios.post("/api/users/forgot-password", {
        email,
      });

      setServerMessage(
        res.data?.message ||
          "If that email exists, a reset link has been sent."
      );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Could not process password reset."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="
        relative min-h-screen overflow-hidden
        bg-[rgb(var(--bg-app))]
        text-[rgb(var(--text-main))]
      "
    >
      {/* Veacon accent */}
      <div
        className="
          h-1.5 w-full
          bg-[linear-gradient(90deg,rgb(var(--brand)),rgb(var(--cool)),rgb(var(--intent)))]
        "
      />

      {/* Background glow */}
      <div
        className="
          pointer-events-none absolute
          left-1/2 top-[-260px]
          h-[560px] w-[560px]
          -translate-x-1/2
          rounded-full blur-3xl
        "
        style={{
          background:
            "radial-gradient(circle, rgb(var(--brand) / 0.10), transparent 68%)",
        }}
      />

      <main
        className="
          relative flex min-h-[calc(100vh-6px)]
          items-center justify-center
          px-4 py-10
          sm:px-6
        "
      >
        <div className="w-full max-w-md">
          {/* Brand */}
          <Link
            to="/"
            aria-label="Veacon home"
            className="
              mx-auto mb-8
              flex w-fit items-center gap-3
            "
          >
            <div
              className="
                flex h-12 w-12 items-center justify-center
                overflow-hidden rounded-2xl
                border border-[rgb(var(--border))]
                bg-[rgb(var(--surface))]
                shadow-[var(--shadow-soft)]
              "
            >
              <img
                src="/logo.svg"
                alt=""
                className="h-7 w-7"
              />
            </div>

            <div>
              <div
                className="
                  text-base font-extrabold
                  tracking-tight
                  text-[rgb(var(--text-main))]
                "
              >
                Veacon
              </div>

              <div
                className="
                  text-xs font-semibold
                  text-[rgb(var(--text-muted))]
                "
              >
                Find things to do.
              </div>
            </div>
          </Link>

          {/* Card */}
          <section
            className="
              rounded-[2rem]
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              p-6
              shadow-[var(--shadow-soft)]
              sm:p-8
            "
          >
            <div className="text-center">
              <div
                className="
                  mx-auto flex h-12 w-12
                  items-center justify-center
                  rounded-2xl
                  bg-[rgb(var(--brand)/0.10)]
                  text-xl
                "
                aria-hidden="true"
              >
                ↗
              </div>

              <h1
                className="
                  mt-5
                  text-3xl font-extrabold
                  tracking-[-0.04em]
                  text-[rgb(var(--text-main))]
                "
              >
                Reset your password
              </h1>

              <p
                className="
                  mx-auto mt-3 max-w-sm
                  text-sm leading-6
                  text-[rgb(var(--text-muted))]
                "
              >
                Enter the email connected to your Veacon
                account and we&apos;ll send you a reset link.
              </p>
            </div>

            {serverMessage ? (
              <div
                className="
                  mt-6 rounded-2xl
                  border border-[rgb(var(--brand)/0.22)]
                  bg-[rgb(var(--brand)/0.07)]
                  p-4
                "
              >
                <div
                  className="
                    text-sm font-extrabold
                    text-[rgb(var(--text-main))]
                  "
                >
                  Check your email
                </div>

                <div
                  className="
                    mt-1 text-sm leading-6
                    text-[rgb(var(--text-muted))]
                  "
                >
                  {serverMessage}
                </div>
              </div>
            ) : null}

            {error ? (
              <div
                className="
                  mt-6 rounded-2xl
                  border border-[rgb(var(--danger)/0.30)]
                  bg-[rgb(var(--danger)/0.08)]
                  p-4
                "
              >
                <div
                  className="
                    text-sm font-extrabold
                    text-[rgb(var(--text-main))]
                  "
                >
                  Couldn&apos;t send reset link
                </div>

                <div
                  className="
                    mt-1 text-sm leading-6
                    text-[rgb(var(--text-muted))]
                  "
                >
                  {error}
                </div>
              </div>
            ) : null}

            <form
              onSubmit={handleSubmit}
              className="mt-7 space-y-5"
            >
              <div>
                <label
                  htmlFor="email"
                  className="
                    text-sm font-bold
                    text-[rgb(var(--text-main))]
                  "
                >
                  Email
                </label>

                <div className="mt-2">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <PrimaryButton disabled={loading}>
                {loading
                  ? "Sending…"
                  : "Send reset link"}
              </PrimaryButton>
            </form>
          </section>

          <div
            className="
              mt-6 text-center
              text-sm
              text-[rgb(var(--text-muted))]
            "
          >
            <Link
              to="/"
              className="
                font-bold
                text-[rgb(var(--text-main))]
                transition
                hover:text-[rgb(var(--brand))]
              "
            >
              ← Back to Veacon
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}